import { Divider, Grid } from '@mui/material';
import { ActionButton, AddItemButton, BasicButton } from '@tassat/button';
import {
    SelectField,
    SimpleErrorMessage
} from '@tassat/formik-input';
import { Delete } from '@tassat/icon';
import { SelectOption } from '@tassat/ui-definitions';
import { FieldArray, Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validateWithdrawalWalletMappingForm } from './submission';
import { IFormProps, IFormValues, IFormikProps } from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        wallets,
        values,
        errors,
        submitForm,
        isSubmitting,
        submitError,
        isValid,
        associatedWallets
    } = props;

    const [ accountOptions, setAccountOptions ] = React.useState<SelectOption[]>([]);
    const unmappedWallets = wallets.length;

    React.useEffect(() => {
        setAccountOptions(
            wallets
                .filter((wallet) => {
                    let notMapped = true;
                    values.Mappings.forEach((mapping) => {
                        if (mapping.Wallet && wallet.AccountId === mapping.Wallet.value) {
                            notMapped = false;
                        }
                    });
                    associatedWallets?.forEach((mapping) => {
                        if (mapping && wallet.AccountId === mapping.AccountId) {
                            notMapped = false;
                        }
                    });

                    return notMapped;
                })
                .map((wallet) => {
                    const walletName = wallet && wallet.AccountName ? wallet.AccountName : '';

                    return {
                        label: `${walletName}`,
                        value: wallet.AccountId,
                        refData: associatedWallets ? associatedWallets : []
                    };
                })
        );
    }, [JSON.stringify(values.Mappings)]);

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <FieldArray
                    name='Mappings'
                    render={(arrayHelpers) => (
                        <>
                            {values.Mappings.map((mapping, index) => (
                                <Grid
                                    container
                                    direction='column'
                                    key={`mapping_${index}`}
                                    wrap='nowrap'
                                >
                                    <Grid
                                        className={classes.fullWidth}
                                        container
                                        item
                                        spacing={3}
                                        wrap='nowrap'
                                        xs={12}
                                    >
                                        <Grid
                                            className={classes.walletSelectWrapper}
                                            container
                                            justifyContent='space-between'
                                        >
                                            <Grid
                                                className={classes.walletSelect}
                                                item
                                                xs='auto'
                                            >
                                                <SelectField
                                                    autoComplete
                                                    errors={errors}
                                                    key={`wallet_select_${index}`}
                                                    label='Select Account'
                                                    name={`Mappings.${index}.Wallet`}
                                                    nestedValue
                                                    OptionRenderer={undefined}
                                                    options={accountOptions}
                                                    SingleSelectValueRenderer={undefined}
                                                    values={values}
                                                />
                                            </Grid>
                                        </Grid>
                                        {values.Mappings.length > 1 &&
                                            (
                                            <Grid
                                                className={classes.removeMappingContainer}
                                                item
                                                xs={1}
                                            >
                                                <ActionButton
                                                    handleClick={() => arrayHelpers.remove(index)}
                                                    Icon={Delete}
                                                    key={`remove_mapping_${index}`}
                                                    text='Delete'
                                                    type='icon'
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                    {values.Mappings.length > 1 &&
                                        index !== values.Mappings.length - 1 &&
                                        (
                                        <Divider
                                            className={classes.fieldMappingDivider}
                                            orientation='horizontal'
                                            variant='fullWidth'
                                        />
                                    )}
                                </Grid>
                            ))}
                            {accountOptions.length > 0 && values.Mappings.length < unmappedWallets &&
                                (
                                <Grid
                                    className={classes.newMappingContainer}
                                    container
                                    item
                                    justifyContent='flex-start'
                                    spacing={2}
                                    xs={12}
                                >
                                    <Grid item>
                                        <AddItemButton
                                            onClick={() => arrayHelpers.push({
                                                Wallet: null,
                                                Role: ''
                                            })}
                                            text='Add New Mapping'
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </>
                    )}
                />
                <Grid
                    container
                    item
                    justifyContent='center'
                    xs={12}
                >
                    <BasicButton
                        className={classes.submitButton}
                        disabled={isSubmitting || !isValid}
                        onClick={submitForm}
                        text='Submit'
                        type='submit'
                    />
                    <SimpleErrorMessage
                        className={classes.submitError}
                        error={submitError}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export function WithdrawalWalletMappingForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        wallets,
        withdrawalInstruction,
        submitWithdrawalWalletMappingForm,
        associatedWallets
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    const initialValues: IFormValues = { Mappings: [{ Wallet: null }]};

    const withdrawalWalletMappingOriginalValidation = (values: IFormValues) => validateWithdrawalWalletMappingForm(values);

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                xs={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        await submitWithdrawalWalletMappingForm(values, {
                            accountWithdrawalDataId: withdrawalInstruction.AccountWithdrawalDataId,
                            setSubmitError,
                            onClose
                        });
                    }}
                    validate={(values: IFormValues) => (withdrawalWalletMappingOriginalValidation(values))}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentsProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentsProps}
                            associatedWallets={associatedWallets}
                            classes={classes}
                            formProps={formProps}
                            submitError={submitError}
                            wallets={wallets}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}